import { useTranslation } from 'react-i18next'

import { Badge, Card, Overline, Section, SectionTitle } from '@/design-system/primitives'

import type { PublicVoteCategory } from './types'

/**
 * Scrutins publics.
 *
 * Deux états seulement : le vote est ouvert (on annonce les nommés), ou les
 * résultats sont publiés (on les affiche). Rien à masquer ici — quand un
 * scrutin n'est pas publié, `results` est absent de la réponse serveur.
 *
 * Seuls les scrutins **libres** figurent ici. Un tour de concours (ou son
 * départage) se lit dans le cumul du concours, tour par tour, public et jury :
 * le répéter ici, en voix seules, afficherait pour le même tour un second
 * classement qui ne compte pas le jury — et contredirait le premier.
 */
export function VoteResultsSection({ categories }: { categories: PublicVoteCategory[] }) {
  const { t } = useTranslation()

  const visible = categories.filter(
    (category) =>
      !category.in_contest && (category.results !== null || category.nominees.length > 0),
  )
  if (visible.length === 0) return null

  return (
    <Section id="vote">
      <Overline>{t('vote.overline')}</Overline>
      <SectionTitle>{t('vote.publicTitle')}</SectionTitle>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {visible.map((category) => (
          <Card key={category.id}>
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="font-display text-heading-md text-content-primary">
                {category.name}
              </h3>
              {category.status === 'open' ? (
                <Badge tone="ember">{t('vote.open')}</Badge>
              ) : category.status === 'published' ? (
                <Badge tone="champagne">{t('vote.published')}</Badge>
              ) : (
                <Badge>{t('vote.frozen')}</Badge>
              )}
            </div>

            {category.results ? (
              <ol className="mt-6 space-y-3">
                {category.results.map((score, _index, all) => (
                  <li
                    key={score.candidate_id ?? score.name}
                    className="flex items-baseline justify-between gap-4"
                  >
                    <span className="flex items-baseline gap-3">
                      <span className="w-5 font-display tabular-nums text-champagne-400">
                        {/* Rang « olympique » : deux ex æquo partagent le
                            même rang, au lieu d'en recevoir deux distincts. */}
                        {all.findIndex((other) => other.votes === score.votes) + 1}
                      </span>
                      <span className="text-content-primary">{score.name}</span>
                    </span>
                    <span className="font-mono text-sm tabular-nums text-content-muted">
                      {score.share}%
                    </span>
                  </li>
                ))}
              </ol>
            ) : (
              <>
                <p className="mt-4 text-sm text-content-muted">{t('vote.resultsPending')}</p>
                <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-content-secondary">
                  {category.nominees.map((nominee) => (
                    <li key={nominee.candidate_id}>
                      {nominee.candidate_number !== null ? (
                        <span className="mr-2 font-mono text-content-muted">
                          #{nominee.candidate_number}
                        </span>
                      ) : null}
                      {nominee.name}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card>
        ))}
      </div>
    </Section>
  )
}
